import { IconsDirective } from '@awork/_shared/directives/icons/icons.directive'
import { ContentRowComponent } from '../content-row/content-row.component'
import type { ContentRowTone } from '../content-row/content-row.component'
import { SwitchButtonComponent } from '../switch-button/switch-button.component'
import { Component, input, output } from '@angular/core'
import { CdkMonitorFocus } from '@angular/cdk/a11y'

/** Trailing control of a content item. */
export type ContentItemControl = 'none' | 'check' | 'switch' | 'picker' | 'chevron' | 'expand' | 'external'

/**
 * Figma "Content item": a row with a 56px minimum height inside a content block with a leading icon or image,
 * a label, and an optional trailing control. The whole row is one button.
 * Project [trailing] content for right-aligned metadata, [value-leading] content, such as an avatar,
 * directly before the label, and [value-trailing] content, such as a count, directly after it. Unselected checks reserve no space.
 * Selector rows support an opt-in --content-item-selector-inset for full-width lists.
 */
@Component({
  selector: 'aw-content-item',
  templateUrl: './content-item.component.html',
  styleUrl: './content-item.component.scss',
  imports: [IconsDirective, ContentRowComponent, SwitchButtonComponent, CdkMonitorFocus]
})
export class ContentItemComponent {
  /** Danger keeps the label and leading icon visible even when the row is disabled or muted. */
  readonly tone = input<ContentRowTone>('default')
  readonly fileName = input<string>()
  readonly variant = input<'default' | 'add-checklist' | 'selector'>('default')
  /** Secondary copy can wrap in selector rows. */
  readonly description = input<string>()
  /** Truncates selector descriptions to one line. Rows grow to fit their content. */
  readonly truncateDescription = input(false)
  /** An outlined hint at the end of a selector row. */
  readonly badge = input<string>()
  readonly initials = input<string>()
  readonly avatarShape = input<'square' | 'round'>('square')
  readonly label = input.required<string>()
  /** Field name shown on the left of a selected custom-field value. */
  readonly fieldName = input<string>()
  /** Includes field context when the visible label is a selected value. */
  readonly ariaLabel = input<string>()
  readonly icon = input<string>()
  /** Uses the entity color for a selector icon, such as a team avatar, or `neutral` for actions such as Clear. */
  readonly iconColor = input<string>()
  /** Draws the icon small on a tinted disc, like a task status. */
  readonly iconBadge = input(false)
  readonly image = input<string>()
  /** Emoji shown as a tinted tile instead of an icon or image. */
  readonly emoji = input<string>()
  readonly control = input<ContentItemControl>('none')
  readonly expanded = input(false)
  /** Highlights the row and shows the check when `control` is `check`. */
  readonly selected = input(false)
  /** Switch position when `control` is `switch`. */
  readonly checked = input(false)
  readonly disabled = input(false)
  /** Renders the label in the secondary color, for placeholders such as "Select project". */
  readonly muted = input(false)

  readonly pressed = output<void>()

  /**
   * Emits the row press.
   */
  onPress(): void {
    this.pressed.emit()
  }
}
